import {
  ComputedDataKey,
  CourseSettingsSourceEnum,
  IPriceDetailLine,
  IRideDto,
  isValidPositiveNumber,
} from "@ryde/shared";

const PASSENGERS_PRICING_LABEL = "passengersPricing";

const isManualPriceSet = (manualPrice?: number | null): boolean =>
  manualPrice !== null && manualPrice !== undefined;

// Mirrors backend shouldUseRideManualClientPrice / shouldUseRideManualSupplierPrice:
// geo rides always honor manual; course rides only in manual pricing mode.
export const shouldUseDisplayedRideManualPrice = ({
  courseId,
  settingsSource,
}: {
  courseId?: number | null;
  settingsSource?: CourseSettingsSourceEnum;
}): boolean => {
  if (!courseId) {
    return true;
  }
  if (settingsSource === undefined) {
    return false;
  }
  return settingsSource !== CourseSettingsSourceEnum.PriceRates;
};

export const hasRideOverrideTotalPriceAddition = ({
  additionIds,
  exclusiveAdditionsIds,
}: {
  additionIds?: number[] | null;
  exclusiveAdditionsIds?: number[] | null;
}): boolean => {
  if (!additionIds?.length || !exclusiveAdditionsIds?.length) {
    return false;
  }
  const exclusiveIds = new Set(exclusiveAdditionsIds);
  return additionIds.some(id => exclusiveIds.has(id));
};

const resolveRideCourseSettingsSource = (
  rideDto?: Partial<IRideDto>,
): CourseSettingsSourceEnum | undefined =>
  rideDto?.courseSettingsSource ?? rideDto?.metadata?.settingsSource;

export const shouldDisplayRidePriceBreakdownLines = ({
  priceRateId,
  manualPrice,
  courseId,
  settingsSource,
  hasOverrideTotalPrice,
}: {
  priceRateId?: number | null;
  manualPrice?: number | null;
  courseId?: number | null;
  settingsSource?: CourseSettingsSourceEnum;
  hasOverrideTotalPrice?: boolean;
}): boolean => {
  if (isValidPositiveNumber(priceRateId) || hasOverrideTotalPrice) {
    return true;
  }
  if (!shouldUseDisplayedRideManualPrice({ courseId, settingsSource })) {
    return false;
  }
  return isManualPriceSet(manualPrice);
};

export const shouldDisplayClientRidePriceBreakdownLines = (
  rideDto?: Partial<IRideDto>,
): boolean =>
  shouldDisplayRidePriceBreakdownLines({
    priceRateId: rideDto?.priceRateId,
    manualPrice: rideDto?.metadata?.manualPrice,
    courseId: rideDto?.courseId,
    settingsSource: resolveRideCourseSettingsSource(rideDto),
    hasOverrideTotalPrice: hasRideOverrideTotalPriceAddition({
      additionIds: rideDto?.additionIds,
      exclusiveAdditionsIds: rideDto?.[ComputedDataKey]?.exclusiveAdditionsIds,
    }),
  });

export const shouldDisplaySupplierRidePriceBreakdownLines = (
  rideDto?: Partial<IRideDto>,
): boolean =>
  shouldDisplayRidePriceBreakdownLines({
    priceRateId: rideDto?.supplierPriceRateId,
    manualPrice: rideDto?.metadata?.supplierManualPrice,
    courseId: rideDto?.courseId,
    settingsSource: resolveRideCourseSettingsSource(rideDto),
  });

// Builds the list of price detail lines to display by aggregating
// passengersPricing rows into a single line when appropriate.
// Rules:
// - If no passengersPricing rows -> return details as-is.
// - If passengersPricing total is 0 -> remove passengersPricing rows.
// - If exactly one passengersPricing row with non-zero value -> keep as-is.
// - If multiple passengersPricing rows -> replace them with a single
//   passengersPricing row whose value equals the sum of their values, and
//   preserve original order by inserting at the position of the first
//   passengersPricing occurrence.
export const buildDisplayedPriceDetails = (
  detailsInput: IPriceDetailLine[] | undefined | null,
): IPriceDetailLine[] => {
  const details: IPriceDetailLine[] = detailsInput ?? [];
  if (!details.length) {
    return [];
  }

  const isPassengerRow = (d: IPriceDetailLine): boolean =>
    d.label === PASSENGERS_PRICING_LABEL;
  const passengerRows = details.filter(isPassengerRow);
  if (passengerRows.length === 0) {
    return details;
  }

  const passengersTotal = passengerRows.reduce(
    (sum, r) => sum + (r.value || 0),
    0,
  );
  if (passengersTotal === 0) {
    return details.filter(d => !isPassengerRow(d));
  }

  if (passengerRows.length === 1) {
    return details;
  }

  let isInserted = false;
  const result: IPriceDetailLine[] = [];
  for (const d of details) {
    if (!isPassengerRow(d)) {
      result.push(d);
      continue;
    }
    if (!isInserted) {
      result.push({
        label: PASSENGERS_PRICING_LABEL,
        value: passengersTotal,
      });
      isInserted = true;
    }
    // skip remaining passengersPricing rows
  }

  return result;
};
